import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Skeleton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider
} from '@mui/material';
import { 
  PlayArrow, 
  Visibility, 
  ThumbUp, 
  Comment, 
  TrendingUp,
  Schedule,
  VideoLibrary,
  People,
  AccessTime
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { youtubeAPI } from '../../services/api';
import KPICard from '../charts/KPICard';
import SectionTitle from '../crt/SectionTitle';
import Panel from '../crt/Panel';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const RealtimeMetrics: React.FC = () => {
  const queryClient = useQueryClient();

  // Fetch recent personal analytics (last 7 days for "real-time" feel)
  const { data: recentAnalytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['youtube-realtime-analytics', 7],
    queryFn: () => youtubeAPI.getPersonalAnalytics(7),
    staleTime: 30000, // 30 seconds for more frequent updates
    refetchInterval: 60000 // Auto-refresh every minute
  });

  // Fetch very recent videos
  const { data: recentVideos, isLoading: videosLoading } = useQuery({
    queryKey: ['recent-videos'],
    queryFn: () => youtubeAPI.getVideos(5, 0, 'published_at', 'desc'),
    staleTime: 30000,
    refetchInterval: 60000
  });

  // Fetch current summary
  const { data: currentSummary, isLoading: summaryLoading } = useQuery({
    queryKey: ['youtube-realtime-summary'],
    queryFn: youtubeAPI.getSummary,
    staleTime: 30000,
    refetchInterval: 60000
  });

  // Fetch comprehensive data for accurate metrics
  const { data: comprehensiveData, isLoading: comprehensiveLoading } = useQuery({
    queryKey: ['youtube-comprehensive-realtime', 7],
    queryFn: () => youtubeAPI.getComprehensiveDashboard(7),
    staleTime: 30000,
    refetchInterval: 60000
  });

  const isLoading = analyticsLoading || videosLoading || summaryLoading || comprehensiveLoading;

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Calculate today's metrics with fallback to comprehensive data
  const todayMetrics = React.useMemo(() => {
    if (!recentAnalytics?.personal_analytics) return null;
    
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    const todayData = recentAnalytics.personal_analytics.find((day: any) => day.date === today);
    const yesterdayData = recentAnalytics.personal_analytics.find((day: any) => day.date === yesterday);
    
    // Use comprehensive data for more accurate weekly metrics
    const weeklyViews = comprehensiveData?.metrics?.period_views || 0;
    const weeklySubscribers = comprehensiveData?.metrics?.period_subscriber_gains || 0;
    const weeklyWatchTime = comprehensiveData?.metrics?.period_watch_time || 0;
    const weeklyEngagement = (comprehensiveData?.metrics?.period_likes || 0) + (comprehensiveData?.metrics?.period_comments || 0);
    
    return {
      todayViews: todayData?.views || 0,
      yesterdayViews: yesterdayData?.views || 0,
      todayWatchTime: todayData ? parseFloat(todayData.estimated_minutes_watched) : 0,
      todayLikes: todayData?.likes || 0,
      todayComments: todayData?.comments || 0,
      todaySubscribers: todayData?.subscribers_gained || 0,
      viewsChange: todayData && yesterdayData ? 
        ((todayData.views - yesterdayData.views) / yesterdayData.views) * 100 : 0,
      weeklyViews,
      weeklySubscribers,
      weeklyWatchTime,
      weeklyEngagement
    };
  }, [recentAnalytics, comprehensiveData]);

  // Auto-refresh indicator
  const [lastUpdate, setLastUpdate] = React.useState(new Date());
  
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
      queryClient.invalidateQueries({ queryKey: ['youtube-realtime-analytics'] });
      queryClient.invalidateQueries({ queryKey: ['recent-videos'] });
      queryClient.invalidateQueries({ queryKey: ['youtube-realtime-summary'] });
      queryClient.invalidateQueries({ queryKey: ['youtube-comprehensive-realtime'] });
    }, 60000); // Refresh every minute

    return () => clearInterval(interval);
  }, [queryClient]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box sx={{ flex: 1 }}>
          <SectionTitle>REAL-TIME METRICS</SectionTitle>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Chip
            icon={<AccessTime />}
            label={`Updated: ${lastUpdate.toLocaleTimeString()}`}
            variant="outlined"
            size="small"
          />
          <Chip
            label="Live"
            color="success"
            size="small"
          />
        </Box>
      </Box>

      {/* Today's Performance */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={160} />
          ) : (
            <KPICard
              title="Today's Views"
              value={todayMetrics?.todayViews || 0}
              change={todayMetrics?.viewsChange || 0}
              format="number"
              icon={<Visibility />}
              color="#1976d2"
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={160} />
          ) : (
            <KPICard
              title="Today's Watch Time"
              value={todayMetrics?.todayWatchTime || 0}
              change={0}
              format="number"
              icon={<Schedule />}
              color="#ff9800"
              subtitle="minutes"
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={160} />
          ) : (
            <KPICard
              title="Today's Engagement"
              value={(todayMetrics?.todayLikes || 0) + (todayMetrics?.todayComments || 0)}
              change={0}
              format="number"
              icon={<ThumbUp />}
              color="#4caf50"
              subtitle="likes + comments"
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={160} />
          ) : (
            <KPICard
              title="New Subscribers"
              value={todayMetrics?.todaySubscribers || 0}
              change={0}
              format="number"
              icon={<People />}
              color="#9c27b0"
              subtitle="today"
            />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Last 7 Days Trend */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Panel label="VIEWS TREND (LAST 7 DAYS)" sx={{ height: 400 }}>
            {isLoading ? (
              <Skeleton variant="rectangular" height={320} />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={recentAnalytics?.personal_analytics || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date"
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis tickFormatter={formatNumber} />
                  <Tooltip 
                    formatter={(value: number) => formatNumber(value)}
                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="views" 
                    stroke="#1976d2" 
                    strokeWidth={3}
                    dot={{ fill: '#1976d2', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#1976d2', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </Grid>

        {/* Recent Activity */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Panel label="RECENT VIDEOS" sx={{ height: 400 }}>
            {isLoading ? (
              <Skeleton variant="rectangular" height={320} />
            ) : (
              <List sx={{ maxHeight: 320, overflow: 'auto' }}>
                {recentVideos?.videos?.slice(0, 4).map((video: any, index: number) => (
                  <React.Fragment key={video.video_id}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar 
                          src={video.thumbnail_url}
                          variant="rounded"
                          sx={{ width: 60, height: 40 }}
                        >
                          <PlayArrow />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                        primary={
                          <Typography variant="body2" fontWeight={600}>
                            {video.title.length > 60 ? `${video.title.substring(0, 60)}...` : video.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" color="textSecondary">
                              {new Date(video.published_at).toLocaleDateString()}
                            </Typography>
                            <Box display="flex" gap={1} mt={0.5}>
                              <Chip 
                                label={`${formatNumber(video.view_count)} views`}
                                size="small"
                                variant="outlined"
                              />
                              <Chip 
                                label={`${video.like_count} likes`}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < 3 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Panel>
        </Grid>
      </Grid>

      {/* Channel Status */}
      <Panel label="CHANNEL STATUS" sx={{ mt: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box textAlign="center">
              <Typography variant="h4" color="primary" fontWeight={600}>
                {formatNumber(currentSummary?.channel?.subscriber_count || 0)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Subscribers
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box textAlign="center">
              <Typography variant="h4" color="primary" fontWeight={600}>
                {formatNumber(currentSummary?.channel?.view_count || 0)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Views
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box textAlign="center">
              <Typography variant="h4" color="primary" fontWeight={600}>
                {currentSummary?.channel?.video_count || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Videos
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box textAlign="center">
              <Typography variant="h4" color="success.main" fontWeight={600}>
                +{formatNumber(currentSummary?.growth?.view_growth || 0)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Recent Growth
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Panel>
    </Box>
  );
};

export default RealtimeMetrics;